import { useState } from 'react';
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from 'lucide-react';
import { cn } from '../../lib/cn.js';

interface PaginationProps {
  page: number;
  totalPages: number;
  total: number;
  pageSize: number;
  onPageChange: (page: number) => void;
}

export function Pagination({ page, totalPages, total, pageSize, onPageChange }: PaginationProps) {
  const [gotoValue, setGotoValue] = useState('');

  if (totalPages <= 1) return null;

  // Build page window: 5 pages around current
  function getPageWindow(): (number | '...')[] {
    const delta = 2;
    const pages: (number | '...')[] = [];

    const rangeStart = Math.max(1, page - delta);
    const rangeEnd = Math.min(totalPages, page + delta);

    if (rangeStart > 1) {
      if (rangeStart > 2) pages.push('...');
    }
    for (let i = rangeStart; i <= rangeEnd; i++) {
      pages.push(i);
    }
    if (rangeEnd < totalPages) {
      if (rangeEnd < totalPages - 1) pages.push('...');
    }

    return pages;
  }

  const window = getPageWindow();

  const from = Math.min((page - 1) * pageSize + 1, total);
  const to = Math.min(page * pageSize, total);

  function handleGoto(e: React.FormEvent) {
    e.preventDefault();
    const n = parseInt(gotoValue, 10);
    if (!isNaN(n) && n >= 1 && n <= totalPages) {
      onPageChange(n);
    }
    setGotoValue('');
  }

  return (
    <div className="flex items-center justify-between gap-3 px-4 py-3 border-t border-border bg-white text-xs text-secondary flex-wrap gap-y-2">
      {/* Left: showing X of Y */}
      <span className="text-xs text-secondary whitespace-nowrap">
        Showing{' '}
        <span className="font-semibold text-primary-text">{from.toLocaleString()}–{to.toLocaleString()}</span>
        {' '}of{' '}
        <span className="font-semibold text-primary-text">{total.toLocaleString()}</span>
      </span>

      {/* Center: page controls */}
      <div className="flex items-center gap-1">
        {/* First */}
        <PageButton
          onClick={() => onPageChange(1)}
          disabled={page <= 1}
          aria-label="First page"
        >
          <ChevronsLeft className="w-3.5 h-3.5" />
        </PageButton>
        {/* Prev */}
        <PageButton
          onClick={() => onPageChange(page - 1)}
          disabled={page <= 1}
          aria-label="Previous page"
        >
          <ChevronLeft className="w-3.5 h-3.5" />
        </PageButton>

        {/* Numbered */}
        {window.map((p, i) =>
          p === '...' ? (
            <span key={`ellipsis-${i}`} className="px-1.5 text-secondary">…</span>
          ) : (
            <PageButton
              key={p}
              active={p === page}
              onClick={() => onPageChange(p as number)}
              aria-label={`Page ${p}`}
              aria-current={p === page ? 'page' : undefined}
            >
              {p}
            </PageButton>
          )
        )}

        {/* Next */}
        <PageButton
          onClick={() => onPageChange(page + 1)}
          disabled={page >= totalPages}
          aria-label="Next page"
        >
          <ChevronRight className="w-3.5 h-3.5" />
        </PageButton>
        {/* Last */}
        <PageButton
          onClick={() => onPageChange(totalPages)}
          disabled={page >= totalPages}
          aria-label="Last page"
        >
          <ChevronsRight className="w-3.5 h-3.5" />
        </PageButton>
      </div>

      {/* Right: Go to page */}
      <form onSubmit={handleGoto} className="flex items-center gap-1.5">
        <span className="text-secondary whitespace-nowrap">Go to</span>
        <input
          type="number"
          min={1}
          max={totalPages}
          value={gotoValue}
          onChange={(e) => setGotoValue(e.target.value)}
          placeholder={String(page)}
          className="w-14 border border-border rounded px-2 py-1 text-xs text-primary-text focus:outline-none focus:border-primary text-center"
          aria-label="Go to page number"
        />
      </form>
    </div>
  );
}

function PageButton({
  children,
  active,
  disabled,
  onClick,
  'aria-label': ariaLabel,
  'aria-current': ariaCurrent,
}: {
  children: React.ReactNode;
  active?: boolean;
  disabled?: boolean;
  onClick: () => void;
  'aria-label'?: string;
  'aria-current'?: 'page' | undefined;
}) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      aria-label={ariaLabel}
      aria-current={ariaCurrent}
      className={cn(
        'min-w-[28px] h-7 px-1.5 rounded text-xs font-medium transition-colors flex items-center justify-center',
        active
          ? 'bg-primary text-white'
          : 'text-secondary hover:bg-hover-bg hover:text-primary-text',
        disabled && 'opacity-40 cursor-not-allowed pointer-events-none'
      )}
    >
      {children}
    </button>
  );
}
